/** SHAREHOLDER_JSON_DATA.transfer_details — 동일 cert_no(양수)에 다수 양도인 */

import { formatMyRegisterPrefixedAddress } from '@/features/register/utils/myRegisterAddressDisplay';
import { parseShareholderJsonRoot } from './myRegisterEntityMemberShareInfo';

export type TransferDetailRow = Record<string, unknown>;

/** reactive proxy 순환 참조 없이 transfer_details 행 복제 */
export function cloneTransferDetailRow(item: Record<string, unknown>): TransferDetailRow {
    try {
        return JSON.parse(JSON.stringify(item)) as TransferDetailRow;
    } catch {
        const out: TransferDetailRow = {};
        for (const [key, value] of Object.entries(item)) {
            if (value !== null && typeof value === 'object') continue;
            out[key] = value;
        }
        return out;
    }
}

export function parseTransferDetailsFromRaw(raw: unknown): TransferDetailRow[] {
    const root = parseShareholderJsonRoot(raw);
    return normalizeTransferDetailsArray(root.transfer_details);
}

export function normalizeTransferDetailsArray(raw: unknown): TransferDetailRow[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((item) => item && typeof item === 'object')
        .map((item) => cloneTransferDetailRow(item as Record<string, unknown>));
}

export function resolveTransferDetailsFromRecord(record: Record<string, unknown>): TransferDetailRow[] {
    const direct = normalizeTransferDetailsArray(record.transfer_details);
    if (direct.length > 0) return direct;
    const rawJson = record.SHAREHOLDER_JSON_DATA ?? record.shareholderJsonData ?? null;
    if (rawJson === record || rawJson === record.transfer_details) return [];
    return parseTransferDetailsFromRaw(rawJson);
}

/** transfer_details가 2건 이상이면 다중 양도인 UI */
export function hasMultiTransferDetails(details: TransferDetailRow[]): boolean {
    return details.length > 1;
}

export function formatTransferDetailAddress(detail: TransferDetailRow): string {
    return formatMyRegisterPrefixedAddress(detail, 'TFOR_');
}

export function formatTransferDetailTeeAddress(detail: TransferDetailRow): string {
    return formatMyRegisterPrefixedAddress(detail, 'TEE_');
}

function pickText(row: TransferDetailRow, ...keys: string[]): string {
    for (const key of keys) {
        const v = row[key];
        if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
}

export function getTransferDetailTforCertNo(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_CERTI_NO', 'TRF_CERTI_NO');
}

export function getTransferDetailTforName(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_NAME', 'TRANSFER_FROM', 'FROM_NAME');
}

export function getTransferDetailTforRegNo(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_REG_NO', 'CONTACT_REG_NO');
}

/** TFOR_CONTACT_CD → Global Contact 이름·등록번호 보강 */
export async function enrichTransferDetailsFromContactApi(
    details: TransferDetailRow[],
    fetchContact: (contactCd: string) => Promise<{ contactName?: string; contactRegNo?: string } | null | undefined>,
): Promise<TransferDetailRow[]> {
    if (details.length === 0) return details;
    const cache = new Map<string, { contactName?: string; contactRegNo?: string }>();

    return Promise.all(details.map(async (detail) => {
        const contactCd = String(detail.TFOR_CONTACT_CD ?? '').trim();
        if (!contactCd) return detail;

        if (!cache.has(contactCd)) {
            const loaded = await fetchContact(contactCd);
            cache.set(contactCd, loaded ?? {});
        }
        const contact = cache.get(contactCd)!;
        const next = { ...detail };
        if (contact.contactName) next.TFOR_NAME = contact.contactName;
        if (contact.contactRegNo) next.TFOR_REG_NO = contact.contactRegNo;
        return next;
    }));
}

export function getTransferDetailTforShares(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_NUMBER_OF_SHARES', 'TRF_NUMBER_OF_SHARES', 'UnitShareCash');
}

/** 양도 수량을 보유(가용) 주식 이하로 제한 */
export function clampTransferDetailTforShares(
    detail: TransferDetailRow,
    maxShares: number,
): TransferDetailRow {
    const row = { ...detail };
    let shares = Number(String(getTransferDetailTforShares(row)).replace(/,/g, ''));
    if (!Number.isFinite(shares) || shares < 1) shares = 1;
    if (maxShares > 0 && shares > maxShares) shares = maxShares;
    const sharesText = String(shares);
    if (String(row.TFOR_NUMBER_OF_SHARES ?? '').trim() === sharesText) return row;
    row.TFOR_NUMBER_OF_SHARES = sharesText;
    row.TEE_NUMBER_OF_SHARES = sharesText;
    row.UnitShareCash = sharesText;
    const price = Number(String(row.TFOR_PRICE_PER_SHARE ?? '').replace(/,/g, ''));
    if (Number.isFinite(price) && Number.isFinite(shares)) {
        const amt = String(shares * price);
        row.NominalAmt = amt;
        row.AmtPaid = amt;
        row.ConsiderAmt = amt;
    }
    return row;
}

/** 저장 직전 — TFOR/TEE 주식수·대금 필드 일치 */
export function normalizeTransferDetailRowSharesForSave(detail: TransferDetailRow): TransferDetailRow {
    const shares = getTransferDetailTforShares(detail);
    const price = getTransferDetailTforPrice(detail) || '1';
    const next: TransferDetailRow = {
        ...detail,
        TFOR_NUMBER_OF_SHARES: shares,
        TEE_NUMBER_OF_SHARES: shares,
        UnitShareCash: shares,
        TFOR_PRICE_PER_SHARE: price,
        TEE_PRICE_PER_SHARE: price,
    };
    const n = Number(shares.replace(/,/g, '')) * Number(price.replace(/,/g, ''));
    if (Number.isFinite(n)) {
        const amt = String(n);
        next.NominalAmt = amt;
        next.AmtPaid = amt;
        next.ConsiderAmt = amt;
    }
    return next;
}

export function getTransferDetailTforPrice(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_PRICE_PER_SHARE');
}

export function getTransferDetailTforFolio(detail: TransferDetailRow): string {
    return pickText(detail, 'TFOR_FOLIO_NO', 'FOLIO_NO', 'FOLIO');
}

export function getTransferDetailConsideration(detail: TransferDetailRow): string {
    const direct = pickText(detail, 'ConsiderAmt', 'AmtPaid', 'NominalAmt');
    if (direct) return direct;
    const shares = Number(String(getTransferDetailTforShares(detail)).replace(/,/g, ''));
    const price = Number(String(getTransferDetailTforPrice(detail) || '').replace(/,/g, ''));
    if (Number.isFinite(shares) && Number.isFinite(price) && shares > 0 && price > 0) {
        const amt = shares * price;
        return Number.isInteger(amt) ? String(amt) : amt.toFixed(2);
    }
    return '';
}

export function getTransferDetailTeeCertNo(detail: TransferDetailRow): string {
    return pickText(detail, 'TEE_CERTI_NO', 'ACQ_CERTI_NO');
}

export function getTransferDetailTeeName(detail: TransferDetailRow): string {
    return pickText(detail, 'TEE_NAME', 'TRANSFER_TO', 'TO_NAME');
}

export function getTransferDetailTeeRegNo(detail: TransferDetailRow): string {
    return pickText(detail, 'TEE_REG_NO');
}

function normalizeCertNoForMatch(cert: unknown): string {
    const digits = String(cert ?? '').trim().replace(/\D/g, '');
    if (!digits) return '';
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? String(n) : String(cert ?? '').trim();
}

export type TransfereeParty = {
    TEE_NAME: string;
    TEE_REG_NO: string;
    TEE_CONTACT_CD: string;
    TEE_CERTI_NO: string;
    TFOR_NUMBER_OF_SHARES: string;
};

/** 양도(Transfer-out) cert — 엔티티 Transfer 이력에서 양수인(TEE) 조회 */
export function resolveTransfereeForTransferOutShare(
    shareRow: Record<string, unknown>,
    entityTransfers: Array<Record<string, unknown>>,
): TransfereeParty | null {
    const tforCertNo = String(
        shareRow.TRF_CERTI_NO
        ?? shareRow.TR_CERTIFICATE_NO
        ?? shareRow.CERT_NO
        ?? shareRow.CERTI_NO
        ?? '',
    ).trim();
    if (!tforCertNo) return null;

    const target = normalizeCertNoForMatch(tforCertNo);
    for (const transfer of entityTransfers) {
        const shareInfo = (transfer.share_info && typeof transfer.share_info === 'object'
            ? transfer.share_info
            : {}) as Record<string, unknown>;
        const held = String(shareInfo.held ?? '').trim().toLowerCase();
        if (held === 'transferor') continue;

        const details = resolveTransferDetailsFromRecord(transfer);
        if (details.length > 0) {
            const matched = details.find((d) => (
                normalizeCertNoForMatch(getTransferDetailTforCertNo(d)) === target
            ));
            if (!matched) continue;
            const teeName = pickText(transfer, 'TEE_NAME');
            const teeReg = pickText(transfer, 'TEE_REG_NO');
            const teeCd = pickText(transfer, 'TEE_CONTACT_CD');
            if (!teeName && !teeReg && !teeCd) continue;
            return {
                TEE_NAME: teeName,
                TEE_REG_NO: teeReg,
                TEE_CONTACT_CD: teeCd,
                TEE_CERTI_NO: pickText(transfer, 'TEE_CERTI_NO', 'CERT_NO', 'ACQ_CERTI_NO'),
                TFOR_NUMBER_OF_SHARES: getTransferDetailTforShares(matched),
            };
        }

        const rootTfor = normalizeCertNoForMatch(pickText(transfer, 'TFOR_CERTI_NO', 'TRF_CERTI_NO'));
        if (rootTfor !== target) continue;
        const teeName = pickText(transfer, 'TEE_NAME');
        const teeReg = pickText(transfer, 'TEE_REG_NO');
        const teeCd = pickText(transfer, 'TEE_CONTACT_CD');
        if (!teeName && !teeReg && !teeCd) continue;
        return {
            TEE_NAME: teeName,
            TEE_REG_NO: teeReg,
            TEE_CONTACT_CD: teeCd,
            TEE_CERTI_NO: pickText(transfer, 'TEE_CERTI_NO', 'CERT_NO', 'ACQ_CERTI_NO'),
            TFOR_NUMBER_OF_SHARES: pickText(transfer, 'TFOR_NUMBER_OF_SHARES', 'TRF_NUMBER_OF_SHARES'),
        };
    }
    return null;
}

export function applyTransfereePartyToShareRow(
    shareRow: Record<string, unknown>,
    party: TransfereeParty | null | undefined,
): void {
    if (!party) return;
    if (party.TEE_NAME) shareRow.TEE_NAME = party.TEE_NAME;
    if (party.TEE_REG_NO) shareRow.TEE_REG_NO = party.TEE_REG_NO;
    if (party.TEE_CONTACT_CD) shareRow.TEE_CONTACT_CD = party.TEE_CONTACT_CD;
    if (party.TEE_CERTI_NO) shareRow.TEE_CERTI_NO = party.TEE_CERTI_NO;
}

export function getTransferDetailTeeShares(detail: TransferDetailRow): string {
    return pickText(detail, 'TEE_NUMBER_OF_SHARES', 'ACQ_NUMBER_OF_SHARES');
}

/** 목록 — 양도인 이름 요약 (2명 이상이면 "+N") */
export function summarizeTransferorNames(details: TransferDetailRow[], fallback = ''): string {
    if (details.length === 0) return fallback;
    const names = details
        .map((d) => getTransferDetailTforName(d))
        .filter(Boolean);
    if (names.length === 0) return fallback;
    if (names.length === 1) return names[0];
    return `${names[0]} (+${names.length - 1})`;
}

/** jsonData에 transfer_details가 없으면 SHAREHOLDER_JSON_DATA에서 보강 */
export function hydrateJsonDataTransferDetails(jsonData: Record<string, unknown>): Record<string, unknown> {
    let next: Record<string, unknown> = { ...jsonData };
    const existing = normalizeTransferDetailsArray(next.transfer_details);
    if (existing.length > 0) {
        next = { ...next, transfer_details: existing };
    } else {
        const parsed = parseTransferDetailsFromRaw(next.SHAREHOLDER_JSON_DATA ?? null);
        if (parsed.length > 0) {
            next = { ...next, transfer_details: parsed };
        }
    }

    // Transfer To Certificate No ↔ share_info.cert_no 통일
    const root = parseShareholderJsonRoot(next.SHAREHOLDER_JSON_DATA ?? null);
    const si = (next.share_info && typeof next.share_info === 'object'
        ? next.share_info
        : root.share_info && typeof root.share_info === 'object'
            ? root.share_info
            : {}) as Record<string, unknown>;
    const pickCert = (...values: unknown[]) => {
        for (const v of values) {
            const s = String(v ?? '').trim();
            if (s) return s;
        }
        return '';
    };
    const certNo = pickCert(
        next.TEE_CERTI_NO,
        next.ACQ_CERTI_NO,
        si.cert_no,
        si.certNo,
        next.CERTI_NO,
        next.CERT_NO,
        root.TEE_CERTI_NO,
        root.ACQ_CERTI_NO,
    );
    if (certNo) {
        next.TEE_CERTI_NO = certNo;
        next.ACQ_CERTI_NO = certNo;
        next.CERTI_NO = certNo;
        next.CERT_NO = certNo;
        next.share_info = { ...si, cert_no: certNo };
    } else if (Object.keys(si).length > 0 && !next.share_info) {
        next.share_info = { ...si };
    }

    // Cash / Otherwise UI 필드 ← share_info
    const cash = pickCert(
        next.CONSIDERATION_PAID_CASH,
        si.cash,
        next.UnitShareCash,
    );
    const otherwise = pickCert(
        next.CONSIDERATION_PAID_OTHERS,
        si.otherwise,
        next.UnitShareOtherwise,
    );
    if (cash) {
        next.CONSIDERATION_PAID_CASH = cash;
        next.share_info = { ...(next.share_info as Record<string, unknown> || si), cash };
    }
    if (otherwise || otherwise === '0') {
        const otherwiseText = otherwise || '0';
        next.CONSIDERATION_PAID_OTHERS = otherwiseText;
        next.share_info = { ...(next.share_info as Record<string, unknown> || si), otherwise: otherwiseText };
    }

    // Number of Shares: 폼 TEE_NUMBER_OF_SHARES 우선 (share_info.total 로 From 합을 덮어쓰지 않음)
    const totalUnits = pickCert(
        next.TEE_NUMBER_OF_SHARES,
        next.ACQ_NUMBER_OF_SHARES,
        next.AC_NUMBER_OF_SHARES,
        si.total_unit_of_shares,
        si.totalUnitOfShares,
    );
    if (totalUnits) {
        next.TEE_NUMBER_OF_SHARES = totalUnits;
        next.AC_NUMBER_OF_SHARES = totalUnits;
        next.ACQ_NUMBER_OF_SHARES = totalUnits;
        next.share_info = {
            ...(next.share_info as Record<string, unknown> || si),
            total_unit_of_shares: totalUnits,
        };
    }

    return next;
}

/** Transfer From 행들의 TFOR 주식 합계 */
export function sumTransferFromShares(details: TransferDetailRow[]): number {
    return details.reduce((sum, d) => {
        const n = Number(String(getTransferDetailTforShares(d)).replace(/,/g, ''));
        return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
}

/** 저장 시 루트 TFOR/TEE를 transfer_details와 동기화 (하위 호환) */
export function syncTransferRootFromDetails(
    jsonData: Record<string, unknown>,
    details: TransferDetailRow[],
): Record<string, unknown> {
    const next: Record<string, unknown> = { ...jsonData, transfer_details: details };
    if (details.length === 0) return next;

    const first = details[0];
    const teeCert = getTransferDetailTeeCertNo(first);
    const teeName = getTransferDetailTeeName(first);
    const teeReg = getTransferDetailTeeRegNo(first);
    const totalShares = sumTransferFromShares(details);

    // 폼에서 수정한 TEE_CERTI_NO 가 있으면 details 값으로 덮지 않음
    if (teeCert && !String(next.TEE_CERTI_NO ?? '').trim()) next.TEE_CERTI_NO = teeCert;
    if (teeName) next.TEE_NAME = teeName;
    if (teeReg) {
        next.TEE_REG_NO = teeReg;
        next.CONTACT_REG_NO = teeReg;
    }
    if (first.TEE_CONTACT_CD) next.TEE_CONTACT_CD = first.TEE_CONTACT_CD;

    /** Transfer To Number of Shares — 비어 있을 때만 From 합 제안 (Save 시 합계 검증) */
    if (totalShares > 0) {
        const totalText = String(totalShares);
        if (!String(next.TEE_NUMBER_OF_SHARES ?? '').trim()) {
            next.TEE_NUMBER_OF_SHARES = totalText;
        }
        next.TFOR_NUMBER_OF_SHARES = totalText;
        if (!String(next.AC_NUMBER_OF_SHARES ?? '').trim()) {
            next.AC_NUMBER_OF_SHARES = totalText;
        }
        if (!String(next.ACQ_NUMBER_OF_SHARES ?? '').trim()) {
            next.ACQ_NUMBER_OF_SHARES = totalText;
        }
    }

    if (hasMultiTransferDetails(details)) {
        const tforNames = summarizeTransferorNames(details);
        if (tforNames) next.TFOR_NAME = tforNames;
        return next;
    }

    const tforName = getTransferDetailTforName(first);
    const tforReg = getTransferDetailTforRegNo(first);
    const tforCert = getTransferDetailTforCertNo(first);
    const tforShares = getTransferDetailTforShares(first);
    if (tforName) next.TFOR_NAME = tforName;
    if (tforReg) next.TFOR_REG_NO = tforReg;
    if (tforCert) next.TFOR_CERTI_NO = tforCert;
    if (tforShares && totalShares <= 0) next.TFOR_NUMBER_OF_SHARES = tforShares;
    if (first.TFOR_CONTACT_CD) next.TFOR_CONTACT_CD = first.TFOR_CONTACT_CD;
    const tforFolio = String(first.TFOR_FOLIO_NO ?? '').trim();
    if (tforFolio) next.TFOR_FOLIO_NO = tforFolio;
    const teeFolio = String(first.TEE_FOLIO_NO ?? '').trim();
    if (teeFolio) next.TEE_FOLIO_NO = teeFolio;

    return next;
}
